import type { FieldChange } from "@/features/configuration/slice";
import type { DeviceKey, DeviceLayout, SelectedCategory, ShowcaseItem } from "../types";

type Translate = (key: string) => string;

/** Voce dell'albero categorie del backend, appiattita da `flattenCategories`. */
export interface CategoryOption {
  name: string;
  identifier?: unknown;
  key?: unknown;
  value?: string;
  path?: string;
  img?: string;
  children?: unknown;
}

interface CategoryNode extends CategoryOption {
  children?: unknown;
}

/** Albero categorie → lista piatta; `value` e `path` descrivono il percorso. Un non-array e' una lista vuota. */
export function flattenCategories(items: unknown): CategoryOption[] {
  const values: CategoryOption[] = [];
  const walk = (item: CategoryNode | null | undefined, parents: string[] = []) => {
    if (!item || !item.name) return;
    const path = [...parents, item.name];
    values.push({
      ...item,
      identifier: item.identifier ?? item.key,
      value: item.value || path.join("/"),
      path: path.join(" / "),
    });
    const children = Array.isArray(item.children) ? (item.children as CategoryNode[]) : [];
    children.forEach((child) => walk(child, path));
  };
  (Array.isArray(items) ? (items as CategoryNode[]) : []).forEach((item) => walk(item));
  return values;
}

/** Chiave di una categoria, sia dalla lista del backend sia dalle voci gia' salvate. */
export function categoryKey(item: CategoryOption | SelectedCategory): unknown {
  const option = item as CategoryOption;
  return option.identifier ?? option.key ?? option.value;
}

export function baseShowcaseOptions(t: Translate, device: DeviceKey): ShowcaseItem[] {
  const prefix = `layoutDesign.${device}`;
  return [
    { text: t(`${prefix}.promotionalBanner`), value: "banner" },
    { text: t(`${prefix}.lastSearches`), value: "lastSearches" },
    { text: t(`${prefix}.recommendationCategories`), value: "recommendationsCategories" },
    { text: t(`${prefix}.mainCategories`), value: "mainCategories" },
    { text: t(`${prefix}.dynamic`), value: "dynamic-1" },
  ];
}

/** Voci di base piu' i blocchi dinamici gia' salvati (tranne `dynamic-1`, gia' nella base), senza doppioni. */
export function showcaseOptions(base: ShowcaseItem[], selected: ShowcaseItem[]): ShowcaseItem[] {
  const dynamic = selected.filter((item) => item.value?.startsWith("dynamic-") && item.value !== "dynamic-1");
  return [...base, ...dynamic].filter(
    (item, index, all) => all.findIndex((entry) => entry.value === item.value) === index,
  );
}

/** Lo showcase non e' disponibile con typing "input", con il layer windowed (solo desktop) o senza piano starter. */
export function isShowcaseDisabled(values: DeviceLayout, device: DeviceKey, starter: boolean): boolean {
  return values.typing === "input" || (device === "desktop" && values.layout === "windowed") || !starter;
}

/** Prossimo blocco dinamico: `dynamic-<max+1>`. */
export function nextDynamicItem(items: ShowcaseItem[], label: string): ShowcaseItem {
  const indexes = items
    .filter((item) => item.value.startsWith("dynamic-"))
    .map((item) => Number(item.value.split("-")[1]) || 0);
  const next = Math.max(0, ...indexes) + 1;
  return { value: `dynamic-${next}`, text: `${label} ${next}` };
}

/** Taglia al limite e porta le categorie scelte nella forma salvata. */
export function normalizeCategories(
  items: (CategoryOption | SelectedCategory)[],
  limit: 12 | 8,
): SelectedCategory[] {
  return items.slice(0, limit).map((raw) => {
    const item = raw as CategoryOption;
    return {
      key: item.identifier ?? item.key,
      value: item.value || item.path || item.name,
      img: item.img || "",
      name: item.name,
    };
  });
}

/** Scelta del layer layout (desktop): `typing` segue il layout, in un'unica scrittura. Percorsi relativi al device. */
export function layerLayoutChanges(value: string): FieldChange[] {
  return [
    { path: ["typing"], value: value === "windowed" ? "input" : "click" },
    { path: ["layout"], value },
  ];
}
